import React, { useState, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Home, Compass, Radio, Plus, Bell, Mail, User, Coins, Settings, Flame, Store, Users, Shield } from "lucide-react";
import { Wordmark, Logo } from "@/components/brand/Logo";
import { AuraRing } from "@/components/brand/AuraRing";
import { CoinIcon } from "@/components/app/AuramaxChip";
import { useAuth } from "@/context/AuthContext";
import { useUnread } from "@/hooks/useUnread";
import { usePoll } from "@/hooks/usePoll";
import { api, auraFmt } from "@/lib/api";
import { CURRENT_USER } from "@/data/mock";
import { cn } from "@/lib/utils";

const items = [
  { to: "/home", label: "For You", icon: Home, testid: "nav-foryou" },
  { to: "/explore", label: "Explore", icon: Compass, testid: "nav-explore" },
  { to: "/live", label: "Live", icon: Radio, testid: "nav-live" },
  { to: "/market", label: "Market", icon: Store, testid: "nav-market" },
  { to: "/leaderboard", label: "Fresh Aura", icon: Flame, testid: "nav-leaderboard" },
  { to: "/notifications", label: "Notifications", icon: Bell, testid: "nav-notifications" },
  { to: "/messages", label: "Messages", icon: Mail, testid: "nav-messages" },
  { to: "/u/me", label: "Profile", icon: User, testid: "nav-profile" },
  { to: "/balance", label: "Balance", icon: CoinIcon, testid: "nav-balance" },
  { to: "/referrals", label: "Invite friends", icon: Users, testid: "nav-referrals" },
  { to: "/token", label: "$AURAMAX", icon: Coins, testid: "nav-token" },
];

export function LeftNav({ onCreate }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const unread = useUnread();
  const [auraCenti, setAuraCenti] = useState(0);
  const loadAura = useCallback(async () => { if (user?.username) { try { const a = await api.auraUser(user.username); setAuraCenti(a.total_centi || 0); } catch {} } }, [user]);
  usePoll(loadAura, 8000, [user]);
  const avatar = (user && user.picture) || CURRENT_USER.avatar;
  const displayName = (user && user.name) || "Your Aura";
  const navItems = user?.is_admin ? [...items, { to: "/admin", label: "Admin", icon: Shield, testid: "nav-admin" }] : items;
  return (
    <aside className="sticky top-0 z-40 hidden h-screen shrink-0 flex-col justify-between overflow-y-auto border-r border-am-border bg-am-bg px-3 py-5 md:flex md:w-[76px] lg:w-[248px] lg:px-4">
      <div className="flex flex-col gap-0.5">
        <button onClick={() => navigate("/home")} className="mb-4 flex items-center gap-2.5 px-2" data-testid="nav-logo">
          <Logo size={34} />
          <span className="hidden font-mark text-lg font-extrabold tracking-tight text-white lg:block">AURAMAX</span>
        </button>

        {navItems.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            data-testid={it.testid}
            className={({ isActive }) =>
              cn(
                "group relative flex items-center gap-4 rounded-xl px-3 py-2 text-[15px] font-medium transition-colors",
                isActive ? "text-white" : "text-am-text2 hover:text-white hover:bg-white/5"
              )
            }
          >
            {({ isActive }) => (
              <>
                {isActive && <span className="absolute left-0 top-1/2 h-6 w-[3px] -translate-y-1/2 rounded-full aura-gradient" />}
                <span className="relative">
                  <it.icon className={cn("h-6 w-6 shrink-0", isActive && "text-white")} strokeWidth={isActive ? 2.4 : 2} />
                  {it.to === "/notifications" && unread > 0 && (
                    <span data-testid="nav-unread" className="absolute -right-1.5 -top-1.5 grid h-4 min-w-4 place-items-center rounded-full aura-gradient px-1 text-[9px] font-bold text-black">{unread > 9 ? "9+" : unread}</span>
                  )}
                </span>
                <span className="hidden lg:block">{it.label}</span>
              </>
            )}
          </NavLink>
        ))}

        <button
          onClick={onCreate}
          data-testid="nav-create"
          className="mt-3 flex items-center justify-center gap-2 rounded-full p-[1.5px] aura-gradient transition-transform hover:scale-[1.02] active:scale-95"
        >
          <span className="flex w-full items-center justify-center gap-2 rounded-full bg-am-bg px-4 py-2.5 lg:bg-transparent">
            <Plus className="h-5 w-5 text-white lg:text-black" strokeWidth={2.8} />
            <span className="hidden font-semibold text-black lg:block">Create</span>
          </span>
        </button>
      </div>

      <button
        onClick={() => navigate("/settings")}
        data-testid="nav-account"
        className="flex items-center gap-3 rounded-xl px-2 py-2 text-left transition-colors hover:bg-white/5"
      >
        <AuraRing src={avatar} size={40} />
        <div className="hidden min-w-0 flex-1 lg:block">
          <div className="truncate text-sm font-semibold text-white">{displayName}</div>
          <div className="truncate text-xs text-am-text2">{auraFmt(auraCenti)} Aura</div>
        </div>
        <Settings className="hidden h-4 w-4 text-am-text2 lg:block" />
      </button>
    </aside>
  );
}
